import { ClockIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import CardContent from "~/components/Card/components/CardContent";
import CardHeader from "~/components/Card/components/CardHeader";
import CardTitle from "~/components/Card/components/CardTitle";
import CardWrapper from "~/components/Card/components/CardWrapper";
import WordIcon from "~/components/icons/WordIcon/WordIcon";
import {
	getInvoiceStatusDisplayNameById,
	invoiceStatusId,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import { updateStaffingUnlocks } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type {
	InvoiceDetails,
	UpdateStaffingUnlocksPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { StaffingUnlockModal } from "~/modules/billing/components/InvoiceDetailsView/components/StaffingUnlockModal.tsx";
import { UnlockedStaffingsSection } from "~/modules/billing/components/InvoiceDetailsView/components/UnlockedStaffingsSection.tsx";
import { useInvoiceDownload } from "~/modules/billing/hooks/useInvoiceDownload.ts";

type Props = {
	canManageInvoices: boolean,
	invoiceData: InvoiceDetails,
	onDownloadEInvoiceClick: () => void,
	onSetInvoiceRecipientClick: () => void,
	setIsBusy: (isBusy: boolean) => void,
	setServerErrorMessage: (message: string) => void
};

export function CurrentInvoiceToDos({
	canManageInvoices,
	invoiceData,
	onDownloadEInvoiceClick,
	onSetInvoiceRecipientClick,
	setIsBusy,
	setServerErrorMessage,
}: Props) {
	const queryClient = useQueryClient();
	const [showUnlockModal, setShowUnlockModal] = useState(false);
	const { isGenerating, handleDownload } = useInvoiceDownload(invoiceData.id);
	const anyStaffingUnlocked = invoiceData.staffingUnlocks.some(staffingUnlock => staffingUnlock.unlocked);
	const isAdvanceInvoice = invoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;
	const hasStaffingUnlocks = invoiceData.isProjectInvoice && !isAdvanceInvoice;
	const isDraftStatus = invoiceData.invoiceStatusId === invoiceStatusId.Draft;
	const isWaitingForApprovalStatus = invoiceData.invoiceStatusId === invoiceStatusId.AwaitingApproval;
	const isApprovedStatus = invoiceData.invoiceStatusId === invoiceStatusId.Approved;
	const isSentToClientStatus = invoiceData.invoiceStatusId === invoiceStatusId.SentToClient;
	const invoiceRecipientIsSet = !!invoiceData.recipientInformation;
	const isSelfBilledInvoice = invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice;

	useEffect(() => {
		if (isGenerating) {
			setIsBusy(true);
		} else {
			setIsBusy(false);
		}
	}, [isGenerating, setIsBusy]);

	/** Sends the requests one after the other. Returns an error message, or `null` when all worked. */
	const submitStaffingUnlocks = useCallback(async (payloads: UpdateStaffingUnlocksPayload[]) => {
		let errorMessage: string | null = null;
		try {
			for (const payload of payloads) {
				try {
					await updateStaffingUnlocks(invoiceData.id, payload);
				} catch (error) {
					console.log(error);
					const staffingIdFields = payload.scope === "staffings"
						? payload.staffingIds.map((_, index) => `staffingIds.${index}`)
						: [];
					errorMessage = getValidationMessage(error, ["invoiceId", ...staffingIdFields]) ?? t("form:unexpectedError");
					break;
				}
			}
		} finally {
			await queryClient.refetchQueries({ queryKey: processedInvoiceQueries.details(invoiceData.id).queryKey });
			if (payloads.some(payload => !payload.unlocked)) {
				queryClient.invalidateQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
			}
		}
		return errorMessage;
	}, [invoiceData.id, queryClient]);

	const handleStaffingUnlocksSubmit = useCallback(async (payloads: UpdateStaffingUnlocksPayload[]) => {
		setIsBusy(true);
		const errorMessage = await submitStaffingUnlocks(payloads);
		if (errorMessage) setServerErrorMessage(errorMessage);
		setIsBusy(false);
	}, [setIsBusy, setServerErrorMessage, submitStaffingUnlocks]);

	const showsStaffingUnlocks = canManageInvoices && isDraftStatus && hasStaffingUnlocks
		&& invoiceData.staffingUnlocks.length > 0;

	const invoiceRecipientListElement = <li>Rechnungsempfänger <button onClick={onSetInvoiceRecipientClick}
																	   className="text-danger-500 hover:underline">ermitteln
		/
		bestimmen</button> und / oder mit verantwortlicher Person verifizieren.
	</li>;

	return <CardWrapper hasTitle={true}>
		<CardHeader>
			<CardTitle>Aktuelle To-dos</CardTitle>
			{isDraftStatus && <div className="ml-auto space-x-2">
				<Button theme="none"
						size="auto"
						className="p-1"
						onClick={handleDownload}><WordIcon className="size-5" /></Button>
				{showsStaffingUnlocks && !anyStaffingUnlocked && <Button theme="danger"
																		 onClick={() => setShowUnlockModal(true)}
																		 className="px-1.5 py-1"
																		 size="auto">
					<ClockIcon className="size-5" />
				</Button>}
			</div>}
			{(isApprovedStatus || isSentToClientStatus) && !isSelfBilledInvoice && <div className="ml-auto space-x-2">
				<Button theme="secondary"
						size="auto"
						className="px-3 py-1"
						onClick={onDownloadEInvoiceClick}>
					<div className="flex items-center gap-x-1">
						<DocumentTextIcon className="size-4" />
						<span className="text-sm">E-Rechnung</span>
					</div>
				</Button>
			</div>}
		</CardHeader>
		<CardContent hasTitle={true}>
			{!anyStaffingUnlocked && <ul className="ml-4 list-outside list-disc text-sm">
				{isDraftStatus && <>
					<li>Rechnungsdokument <button onClick={handleDownload}
												  className="text-danger-500 hover:underline">herunterladen</button>,
						prüfen und verwantwortlicher Person zur Freigabe vorlegen.
					</li>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					{showsStaffingUnlocks &&
						<li>Falls noch Änderungen an den zugrundeliegenden Erfassungen notwendig sind, können
							diese <button onClick={() => setShowUnlockModal(true)}
										  className="text-danger-500 hover:underline">temporär entsperrt
								werden</button>.
						</li>}
					<li>
						Nach Abschluss aller vorherigen Schritte den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.AwaitingApproval)}&quot; setzen.
					</li>
				</>}

				{isWaitingForApprovalStatus && <>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					<li>Freigabe des Rechnungsdokuments durch die verantwortliche Person abwarten.</li>
					<li>Nach Freigabe den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.Approved)}&quot; ändern.
					</li>
					<li>Optional: Namen der verantwortlichen Person in einem Kommentart hinterlegen.</li>
				</>}

				{isApprovedStatus && <>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					<li>Abschließend prüfen, ob alle relevanten Informationen mit dem Rechnungsdokument übereinstimmen
						und ggf. hier korrigieren.
					</li>
					<li>Rechnung an Rechnungsempfänger übermitteln.</li>
					<li>Anschließend den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.SentToClient)}&quot; ändern.
					</li>
					<li><strong>Bitte beachten: </strong>Das Versanddatum wird bei der Statusänderung automatisch auf
						den heutigen Tag gesetzt. Sollte das tatsächliche Datum abweichen,
						kann es anschließend in der Zahlungsdetails-Sektion korrigiert werden.
					</li>
				</>}

				{isSentToClientStatus && <>
					<li>Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.Paid)}&quot; ändern, sobald der
						Zahlungseingang festgestellt wurde.
					</li>
					<li><strong>Bitte beachten: </strong>Das Zahlungseingangsdatum wird bei der Statusänderung
						automatisch auf den heutigen Tag gesetzt. Sollte das tatsächliche Datum abweichen,
						kann es anschließend in der Zahlungsdetails-Sektion korrigiert werden.
					</li>
				</>}
			</ul>}
			{anyStaffingUnlocked && <UnlockedStaffingsSection
				staffingUnlocks={invoiceData.staffingUnlocks}
				onSubmit={showsStaffingUnlocks ? handleStaffingUnlocksSubmit : undefined} />}
		</CardContent>
		{showsStaffingUnlocks && <StaffingUnlockModal open={showUnlockModal}
													  staffingUnlocks={invoiceData.staffingUnlocks}
													  onClose={() => setShowUnlockModal(false)}
													  onSubmit={submitStaffingUnlocks} />}
	</CardWrapper>;
}